import { useEffect, useState } from "react";

/**
 * Carte de suivi VORTEX — WEB, état ACTIF UNIQUEMENT (rendue seulement si une vraie
 * livraison VORTEX est en cours). L'overlay est TRANSPARENT : l'illustration gravée
 * (scooter + route) de votre image ACTIVE reste visible. On remplace UNIQUEMENT les
 * valeurs dynamiques gravées (titre/réf, livreur, étape, ETA) par les VRAIES données.
 * L'état « sans commande » n'utilise plus d'overlay : c'est l'image ATTENTE gravée.
 */
const GREEN = "#2BD66A";
const STEPS = ["Validée", "Préparation", "Livraison", "Livrée"];
const STEP_BY_STATUS = { pending: 0, assigned: 1, ready: 1, in_transit: 2, delivered: 3, cancelled: 3 };

// Boîtes de recouvrement (fractions de la carte) — calées sur les gabarits gravés JOUR/NUIT.
const COORDS = {
  day: {
    ref: { l: 0.66, t: 0.012, w: 0.33, h: 0.115 },
    driver: { l: 0.035, t: 0.14, w: 0.37, h: 0.23 },
    bottom: { l: 0, t: 0.745, w: 1, h: 0.255 },
    etaInBottom: true,
  },
  night: {
    ref: { l: 0.64, t: 0.035, w: 0.35, h: 0.11 },
    driver: { l: 0.025, t: 0.165, w: 0.43, h: 0.205 },
    eta: { l: 0.72, t: 0.595, w: 0.26, h: 0.18 },
    bottom: { l: 0, t: 0.77, w: 1, h: 0.23 },
    etaInBottom: false,
  },
};

function etaMinutes(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.round((t - Date.now()) / 60000));
}

const CSS = `
.ptc{position:absolute;inset:0;cursor:pointer;-webkit-tap-highlight-color:transparent;overflow:hidden;}
.ptc-panel{position:absolute;box-shadow:0 0 16px rgba(43,214,106,.3);}
.ptc-refchip{display:flex;align-items:center;justify-content:center;gap:5px;border-radius:999px;
  background:rgba(4,18,10,0.96);border:1px solid rgba(43,214,106,.45);padding:0 10px;}
.ptc-ref{color:#fff;font-size:min(3vw,13px);font-weight:900;white-space:nowrap;}
.ptc-chev{color:${GREEN};font-size:min(3.6vw,15px);font-weight:900;}

.ptc-driver{display:flex;align-items:center;gap:7px;border-radius:999px;padding:0 9px;
  background:rgba(5,22,12,0.97);border:1.5px solid rgba(43,214,106,.65);}
.ptc-avatar{width:30%;max-width:40px;aspect-ratio:1;border-radius:999px;border:2px solid ${GREEN};object-fit:cover;background:#06220F;
  display:flex;align-items:center;justify-content:center;color:${GREEN};font-weight:900;flex:0 0 auto;}
.ptc-dmeta{min-width:0;flex:1;}
.ptc-dlabel{color:rgba(255,255,255,.72);font-size:min(2.6vw,11px);font-weight:700;line-height:1.1;}
.ptc-dname{color:#fff;font-size:min(3.8vw,16px);font-weight:900;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;line-height:1.15;}
.ptc-call{width:clamp(22px,7%,30px);aspect-ratio:1;border-radius:999px;background:${GREEN};border:0;display:flex;align-items:center;
  justify-content:center;cursor:pointer;color:#06220F;font-weight:900;flex:0 0 auto;}

.ptc-eta{display:flex;align-items:center;justify-content:center;gap:6px;border-radius:999px;
  background:rgba(5,22,12,0.97);border:1.5px solid ${GREEN};}
.ptc-eta b{color:${GREEN};font-size:min(5vw,22px);font-weight:900;line-height:1;}

.ptc-bottom{display:flex;align-items:center;border-radius:14px;padding:0 3%;background:rgba(4,18,10,0.985);}
.ptc-steps{flex:1;display:flex;align-items:flex-start;justify-content:space-between;position:relative;}
.ptc-line{position:absolute;top:8px;left:11%;right:11%;height:2px;background:rgba(255,255,255,.25);}
.ptc-step{display:flex;flex-direction:column;align-items:center;flex:1;z-index:1;}
.ptc-sdot{width:18px;height:18px;border-radius:999px;background:#0b2416;border:2px solid rgba(255,255,255,.55);
  display:flex;align-items:center;justify-content:center;color:#06220F;font-size:10px;font-weight:900;}
.ptc-sdot.on{background:${GREEN};border-color:${GREEN};box-shadow:0 0 10px ${GREEN};}
.ptc-slabel{color:#fff;font-size:min(3vw,12px);font-weight:800;margin-top:5px;white-space:nowrap;}
.ptc-slabel.on{color:${GREEN};}
.ptc-etabox{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:0 3%;border-left:1px solid rgba(43,214,106,.3);min-width:28%;}
.ptc-etabox small{color:rgba(255,255,255,.82);font-size:min(2.7vw,11px);font-weight:700;}
.ptc-etabox b{color:${GREEN};font-size:min(6vw,26px);font-weight:900;line-height:1;}
`;

function pct(z) {
  return { left: `${z.l * 100}%`, top: `${z.t * 100}%`, width: `${z.w * 100}%`, height: `${z.h * 100}%` };
}

export default function PrefaceTrackingCard({ track, orderRef, backend, mode = "day", onClick }) {
  const C = COORDS[mode] || COORDS.day;
  const prof = track?.driver_profile ?? null;
  const step = STEP_BY_STATUS[track?.status] ?? 2;
  const [mins, setMins] = useState(etaMinutes(track?.eta_p50));

  useEffect(() => {
    setMins(etaMinutes(track?.eta_p50));
    const id = setInterval(() => setMins(etaMinutes(track?.eta_p50)), 15000);
    return () => clearInterval(id);
  }, [track?.eta_p50]);

  const driverName = prof?.first_name || "Livreur";
  const etaLabel = mins != null ? `${mins} min` : "—";

  return (
    <div className="ptc" data-testid="active-tracking-card" onClick={onClick} role="button" tabIndex={0}>
      <style>{CSS}</style>

      {/* Réf commande réelle (recouvre la réf gravée, garde le titre gravé visible) */}
      <div className="ptc-panel ptc-refchip" style={pct(C.ref)}>
        <span className="ptc-ref">{orderRef || "#—"}</span>
        <span className="ptc-chev">›</span>
      </div>

      {/* Pastille livreur réel (recouvre la pastille gravée) */}
      <div className="ptc-panel ptc-driver" style={pct(C.driver)}>
        {prof?.photo_url ? (
          <img className="ptc-avatar" src={`${backend}${prof.photo_url}`} alt="" />
        ) : (
          <span className="ptc-avatar">{driverName.charAt(0).toUpperCase()}</span>
        )}
        <div className="ptc-dmeta">
          <div className="ptc-dlabel">Votre livreur</div>
          <div className="ptc-dname">{driverName}</div>
        </div>
        {!!prof?.phone && (
          <button className="ptc-call" data-testid="active-tracking-call"
            onClick={(e) => { e.stopPropagation(); window.location.href = `tel:${prof.phone}`; }}>✆</button>
        )}
      </div>

      {/* ETA NUIT (pastille interne séparée) */}
      {mode === "night" && (
        <div className="ptc-panel ptc-eta" style={pct(C.eta)}>
          <span style={{ color: GREEN, fontSize: "min(4vw,18px)" }}>⏱</span>
          <b>{etaLabel}</b>
        </div>
      )}

      {/* Barre basse (recouvre étapes + ETA gravées) */}
      <div className="ptc-panel ptc-bottom" style={pct(C.bottom)}>
        <div className="ptc-steps">
          <div className="ptc-line" />
          {STEPS.map((label, i) => (
            <div className="ptc-step" key={label}>
              <div className={"ptc-sdot" + (i <= step ? " on" : "")}>{i < step ? "✓" : ""}</div>
              <div className={"ptc-slabel" + (i === step ? " on" : "")}>{label}</div>
            </div>
          ))}
        </div>
        {C.etaInBottom && (
          <div className="ptc-etabox">
            <small>Arrivée estimée</small>
            <b>{etaLabel}</b>
          </div>
        )}
      </div>
    </div>
  );
}
